import { useState, useMemo, useEffect } from 'react';
import { ChevronDown, ArrowLeftRight } from 'lucide-react';
import type { SemData, Campaign, AdGroup } from '@/types/sem';
import type { LandingPageReport, LandingPageAnalysis } from '@/types/landing-page-analysis';
import type { ComparisonSide, ComparisonSelection } from '@/types/comparison';
import { normalizeRestaurantName, normalizeAdGroupName, normalizeCampaignName } from '@/lib/normalize-names';
import { ComparisonFunnel } from './ComparisonFunnel';
import { ComparisonScoreCard } from './ComparisonScoreCard';
import { ComparisonDiagnosis } from './ComparisonDiagnosis';

// ============================================
// Ad Group Comparison — Side-by-side analysis
// ============================================

interface AdGroupComparisonProps {
  semData: SemData;
  lpReport?: LandingPageReport | null;
}

// Parse restaurant name from campaign name (format: "Restaurant Name - Campaign Type")
function parseRestaurantName(campaignName: string): string {
  const match = campaignName.match(/^(.+?)\s*-\s*.+$/);
  return match ? match[1].trim() : campaignName;
}

// Group campaigns by restaurant, filtering out $0 spend campaigns and ad groups
function groupByRestaurant(campaigns: Campaign[]): Map<string, Campaign[]> {
  const groups = new Map<string, Campaign[]>();
  for (const campaign of campaigns) {
    if (campaign.metrics.spend <= 0) continue;
    // Filter ad groups with $0 spend
    const activeAdGroups = campaign.adGroups.filter(ag => ag.metrics.spend > 0);
    if (activeAdGroups.length === 0) continue;
    const filtered = { ...campaign, adGroups: activeAdGroups };
    const restaurant = parseRestaurantName(campaign.name);
    const existing = groups.get(restaurant) || [];
    existing.push(filtered);
    groups.set(restaurant, existing);
  }
  return groups;
}

// Find landing page for a campaign
function findLandingPage(
  lpReport: LandingPageReport | null | undefined,
  campaignName: string,
  adGroup?: AdGroup
): LandingPageAnalysis | undefined {
  if (!lpReport?.pages) return undefined;

  // Try to match by campaign name
  const byName = lpReport.pages.find(p =>
    p.campaignName?.toLowerCase().includes(campaignName.toLowerCase()) ||
    campaignName.toLowerCase().includes(p.campaignName?.toLowerCase() || '')
  );
  if (byName) return byName;

  // Try to match by ad's final URL
  if (adGroup?.ads?.[0]?.finalUrl) {
    const url = adGroup.ads[0].finalUrl;
    return lpReport.pages.find(p => p.url === url || p.url.includes(url) || url.includes(p.url));
  }

  return undefined;
}

// Dropdown component
function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
}: {
  value: string | null;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  disabled?: boolean;
}) {
  return (
    <div className="relative">
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`
          w-full appearance-none rounded-lg border border-[var(--ed-rule)] bg-[var(--ed-bg-2)]
          px-4 py-2.5 pr-10 text-sm
          focus:outline-none focus:ring-2 focus:ring-[var(--ed-rule-heavy)] focus:border-[var(--ed-rule-heavy)]
          disabled:opacity-50 disabled:cursor-not-allowed
          ${!value ? 'text-muted-foreground' : ''}
        `}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
    </div>
  );
}

// Side selector panel
function SideSelector({
  label,
  selection,
  onSelectionChange,
  restaurantGroups,
}: {
  label: string;
  selection: ComparisonSelection;
  onSelectionChange: (selection: ComparisonSelection) => void;
  restaurantGroups: Map<string, Campaign[]>;
}) {
  const restaurants = Array.from(restaurantGroups.keys()).sort();

  const selectedCampaigns = selection.restaurantId
    ? restaurantGroups.get(selection.restaurantId) || []
    : [];

  const selectedAdGroups = selection.campaignId
    ? selectedCampaigns.find(c => c.id === selection.campaignId)?.adGroups || []
    : [];

  return (
    <div className="space-y-3">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</span>

      <Select
        value={selection.restaurantId}
        onChange={(value) => onSelectionChange({
          restaurantId: value || null,
          campaignId: null,
          adGroupId: null,
        })}
        options={restaurants.map(r => ({ value: r, label: r }))}
        placeholder="Select restaurant..."
      />

      <Select
        value={selection.campaignId}
        onChange={(value) => onSelectionChange({
          ...selection,
          campaignId: value || null,
          adGroupId: null,
        })}
        options={selectedCampaigns.map(c => ({
          value: c.id,
          label: normalizeCampaignName(c.name.replace(`${selection.restaurantId} - `, '')),
        }))}
        placeholder="Select campaign..."
        disabled={!selection.restaurantId}
      />

      <Select
        value={selection.adGroupId}
        onChange={(value) => onSelectionChange({
          ...selection,
          adGroupId: value || null,
        })}
        options={selectedAdGroups.map(ag => ({
          value: ag.id,
          label: normalizeAdGroupName(ag.name),
        }))}
        placeholder="Select ad group..."
        disabled={!selection.campaignId}
      />
    </div>
  );
}

// Comparison column
function ComparisonColumn({
  restaurantName,
  campaign,
  adGroup,
  landingPage,
}: {
  restaurantName: string;
  campaign: Campaign;
  adGroup: AdGroup;
  landingPage?: LandingPageAnalysis;
}) {
  return (
    <div className="flex-1 min-w-0 space-y-4">
      {/* Header */}
      <div className="pb-3 border-b border-border/30">
        <h3 className="text-lg font-medium">{restaurantName}</h3>
        <p className="text-sm text-muted-foreground">
          {normalizeCampaignName(campaign.name.replace(`${restaurantName} - `, ''))} &middot; {normalizeAdGroupName(adGroup.name)}
        </p>
      </div>

      {/* Funnel */}
      <div className="ed-card p-4">
        <h4 className="ed-section-label mb-4">
          Ad Performance
        </h4>
        <ComparisonFunnel adGroup={adGroup} showDiagnosis />
      </div>

      {/* Landing Page Scores */}
      <div className="ed-card p-4">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-4">
          Landing Page Scores
        </h4>
        <ComparisonScoreCard landingPage={landingPage} showIssues />
      </div>
    </div>
  );
}

export function AdGroupComparison({ semData, lpReport }: AdGroupComparisonProps) {
  // Group campaigns by restaurant
  const restaurantGroups = useMemo(
    () => groupByRestaurant(semData.campaigns),
    [semData.campaigns]
  );

  // Selection state
  const [leftSelection, setLeftSelection] = useState<ComparisonSelection>({
    restaurantId: null,
    campaignId: null,
    adGroupId: null,
  });

  const [rightSelection, setRightSelection] = useState<ComparisonSelection>({
    restaurantId: null,
    campaignId: null,
    adGroupId: null,
  });

  // Auto-populate best vs worst ROAS restaurant on mount
  useEffect(() => {
    if (leftSelection.restaurantId || rightSelection.restaurantId) return;
    const entries = Array.from(restaurantGroups.entries());
    if (entries.length < 2) return;

    // Find best and worst by ad group ROAS (with spend > 0)
    let bestEntry: { restaurant: string; campaign: Campaign; adGroup: AdGroup; roas: number } | null = null;
    let worstEntry: { restaurant: string; campaign: Campaign; adGroup: AdGroup; roas: number } | null = null;

    for (const [restaurant, campaigns] of entries) {
      for (const campaign of campaigns) {
        for (const ag of campaign.adGroups) {
          if (ag.metrics.spend <= 0 || ag.metrics.clicks < 10) continue;
          const roas = ag.metrics.roas;
          if (!bestEntry || roas > bestEntry.roas) bestEntry = { restaurant, campaign, adGroup: ag, roas };
          if (!worstEntry || (roas < worstEntry.roas && roas >= 0)) worstEntry = { restaurant, campaign, adGroup: ag, roas };
        }
      }
    }

    if (bestEntry && worstEntry && bestEntry.adGroup.id !== worstEntry.adGroup.id) {
      setLeftSelection({ restaurantId: bestEntry.restaurant, campaignId: bestEntry.campaign.id, adGroupId: bestEntry.adGroup.id });
      setRightSelection({ restaurantId: worstEntry.restaurant, campaignId: worstEntry.campaign.id, adGroupId: worstEntry.adGroup.id });
    }
  }, [restaurantGroups, leftSelection.restaurantId, rightSelection.restaurantId]);

  // Resolve selections to actual data
  const resolveSelection = (selection: ComparisonSelection): ComparisonSide | null => {
    if (!selection.restaurantId || !selection.campaignId || !selection.adGroupId) return null;

    const campaigns = restaurantGroups.get(selection.restaurantId);
    if (!campaigns) return null;

    const campaign = campaigns.find(c => c.id === selection.campaignId);
    if (!campaign) return null;

    const adGroup = campaign.adGroups.find(ag => ag.id === selection.adGroupId);
    if (!adGroup) return null;

    const landingPage = findLandingPage(lpReport, campaign.name, adGroup);

    return {
      restaurant: { id: selection.restaurantId, name: selection.restaurantId },
      campaign,
      adGroup,
      landingPage,
    };
  };

  const leftSide = resolveSelection(leftSelection);
  const rightSide = resolveSelection(rightSelection);
  const canCompare = leftSide && rightSide;

  return (
    <div className="space-y-6">
      {/* Selector Row */}
      <div className="ed-card p-6">
        <div className="flex items-center gap-4 mb-6">
          <ArrowLeftRight className="h-5 w-5 text-muted-foreground" />
          <div>
            <h2 className="text-lg font-medium">Compare Ad Groups + Landing Pages</h2>
            <p className="text-sm text-muted-foreground">
              Select two ad groups to compare their performance and landing page quality side-by-side
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <SideSelector
            label="Restaurant A"
            selection={leftSelection}
            onSelectionChange={setLeftSelection}
            restaurantGroups={restaurantGroups}
          />

          <SideSelector
            label="Restaurant B"
            selection={rightSelection}
            onSelectionChange={setRightSelection}
            restaurantGroups={restaurantGroups}
          />
        </div>
      </div>

      {/* Comparison Content */}
      {canCompare ? (
        <>
          {/* Side-by-side columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ComparisonColumn
              restaurantName={normalizeRestaurantName(leftSide.restaurant.name)}
              campaign={leftSide.campaign}
              adGroup={leftSide.adGroup}
              landingPage={leftSide.landingPage}
            />

            <ComparisonColumn
              restaurantName={normalizeRestaurantName(rightSide.restaurant.name)}
              campaign={rightSide.campaign}
              adGroup={rightSide.adGroup}
              landingPage={rightSide.landingPage}
            />
          </div>

          {/* Diagnosis */}
          <ComparisonDiagnosis left={leftSide} right={rightSide} />
        </>
      ) : (
        <div className="ed-card p-12 text-center">
          <ArrowLeftRight className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground">Select Ad Groups to Compare</h3>
          <p className="text-sm text-muted-foreground/70 mt-2 max-w-md mx-auto">
            Choose a restaurant, campaign, and ad group for each side to see a side-by-side comparison
            of their funnel performance and landing page quality.
          </p>
        </div>
      )}
    </div>
  );
}
